// tensor's four default views (design 02 3.6), computed here from the rows
// read into the device: 이번 달 분류별 (예산 대비), 월별 추이, 사람별 and
// 결제수단별. Each device computes them again from the rows (design 02
// 4.4: only the inputs travel).
//
// Rules: an expense row counts to 지출 and an income row to 수입. A
// category rolls up into its parent (식비 > 장보기 counts to 식비). An item
// that was deleted keeps its name (rows still point at it); a row without
// an item, or with one this device does not know, goes under "(없음)".
// A top-level category's budget is its own, or else the sum of its
// children's budgets.
import type { Id, TensorBudget, TensorItem, TensorRole } from './api'
import { addMonths } from './format'
import { budgetKey } from './model'
import type { RowFields } from './model'

export const NONE = '(없음)'

/** What a view needs of a row. */
export type ViewRow = Pick<RowFields, 'kind' | 'day' | 'amount' | 'person_item' | 'category_item' | 'payment_item'>

export interface Totals {
  income: number
  expense: number
  /** income - expense */
  balance: number
}

export function totals(rows: readonly ViewRow[]): Totals {
  let income = 0
  let expense = 0
  for (const r of rows) {
    if (r.kind === 'income') income += r.amount
    else expense += r.amount
  }
  return { income, expense, balance: income - expense }
}

export function rowsOfMonth<T extends ViewRow>(rows: readonly T[], month: string): T[] {
  const p = `${month}-`
  return rows.filter((r) => r.day.startsWith(p))
}

function byOrder(a: TensorItem, b: TensorItem): number {
  return a.position - b.position || a.id - b.id
}

/** The living items of a dimension under parent (0: top level), in order. */
export function itemsOf(items: Record<number, TensorItem>, role: TensorRole, parent: Id = 0): TensorItem[] {
  return Object.values(items)
    .filter((i) => i.role === role && !i.deleted && (i.parent_id ?? 0) === parent)
    .sort(byOrder)
}

/** An item's name ("(없음)" for none or unknown). */
export function itemName(items: Record<number, TensorItem>, id: Id | null): string {
  if (!id) return NONE
  return items[id]?.name ?? NONE
}

/** "식비 > 장보기" (or "식비" for a top-level one). */
export function categoryPath(items: Record<number, TensorItem>, id: Id | null): string {
  const it = id ? items[id] : undefined
  if (!it) return NONE
  const parent = it.parent_id ? items[it.parent_id] : undefined
  return parent ? `${parent.name} > ${it.name}` : it.name
}

/** The top-level category a category counts to (0: none or unknown). */
export function topOf(items: Record<number, TensorItem>, id: Id | null): Id {
  const it = id ? items[id] : undefined
  if (!it) return 0
  return it.parent_id && items[it.parent_id] ? it.parent_id : it.id
}

export function budgetOf(budgets: Record<string, TensorBudget>, month: string, category: Id): number {
  return budgets[budgetKey(month, category)]?.amount ?? 0
}

export interface CategoryLine {
  /** the item, 0 for "(없음)" */
  id: Id
  name: string
  spent: number
  budget: number
  /** budget - spent (negative: over) */
  left: number
  /** under a top-level line: its children with spending or a budget */
  children: CategoryLine[]
}

export interface CategoryView {
  lines: CategoryLine[]
  spent: number
  budget: number
}

/** 이번 달 분류별 (예산 대비): the month's spending per top-level category
    against its budget; categories with neither are left out. */
export function byCategory(
  rows: readonly ViewRow[],
  items: Record<number, TensorItem>,
  budgets: Record<string, TensorBudget>,
  month: string,
): CategoryView {
  const spentBy = new Map<Id, number>()
  for (const r of rowsOfMonth(rows, month)) {
    if (r.kind !== 'expense') continue
    const id = r.category_item && items[r.category_item] ? r.category_item : 0
    spentBy.set(id, (spentBy.get(id) ?? 0) + r.amount)
  }
  const cats = Object.values(items)
    .filter((i) => i.role === 'category')
    .sort(byOrder)
  const lines: CategoryLine[] = []
  let spent = 0
  let budget = 0
  for (const top of cats) {
    if (top.parent_id && items[top.parent_id]) continue
    const children: CategoryLine[] = []
    let childBudgets = 0
    let topSpent = spentBy.get(top.id) ?? 0
    for (const c of cats) {
      if (c.parent_id !== top.id) continue
      const s = spentBy.get(c.id) ?? 0
      const b = c.deleted ? 0 : budgetOf(budgets, month, c.id)
      topSpent += s
      childBudgets += b
      if (s > 0 || b > 0) children.push({ id: c.id, name: c.name, spent: s, budget: b, left: b - s, children: [] })
    }
    const own = top.deleted ? 0 : budgetOf(budgets, month, top.id)
    const b = own > 0 ? own : childBudgets
    if (topSpent === 0 && b === 0) continue
    lines.push({ id: top.id, name: top.name, spent: topSpent, budget: b, left: b - topSpent, children })
    spent += topSpent
    budget += b
  }
  const none = spentBy.get(0) ?? 0
  if (none > 0) {
    lines.push({ id: 0, name: NONE, spent: none, budget: 0, left: -none, children: [] })
    spent += none
  }
  return { lines, spent, budget }
}

export interface MonthLine extends Totals {
  month: string
}

/** 월별 추이: n months up to `end`, oldest first. */
export function trend(rows: readonly ViewRow[], end: string, n = 12): MonthLine[] {
  const first = addMonths(end, -(n - 1))
  const lines: MonthLine[] = []
  const at = new Map<string, MonthLine>()
  for (let i = 0; i < n; i++) {
    const month = addMonths(first, i)
    const l = { month, income: 0, expense: 0, balance: 0 }
    lines.push(l)
    at.set(month, l)
  }
  for (const r of rows) {
    const l = at.get(r.day.slice(0, 7))
    if (!l) continue
    if (r.kind === 'income') l.income += r.amount
    else l.expense += r.amount
  }
  for (const l of lines) l.balance = l.income - l.expense
  return lines
}

export interface DimLine {
  id: Id
  name: string
  expense: number
  income: number
}

/** 사람별 / 결제수단별: the month's spending and income per item. */
export function byDimension(
  rows: readonly ViewRow[],
  items: Record<number, TensorItem>,
  role: 'person' | 'payment',
  month: string,
): DimLine[] {
  const key = role === 'person' ? 'person_item' : 'payment_item'
  const at = new Map<Id, DimLine>()
  for (const r of rowsOfMonth(rows, month)) {
    const v = r[key]
    const id = v && items[v] ? v : 0
    let l = at.get(id)
    if (!l) {
      l = { id, name: id ? items[id]!.name : NONE, expense: 0, income: 0 }
      at.set(id, l)
    }
    if (r.kind === 'income') l.income += r.amount
    else l.expense += r.amount
  }
  return [...at.values()].sort((a, b) => {
    if (a.id === 0 || b.id === 0) return a.id === 0 ? 1 : -1
    return byOrder(items[a.id]!, items[b.id]!)
  })
}

/** The share of a bar, 0..1 (over the budget: 1). */
export function share(part: number, whole: number): number {
  if (whole <= 0) return part > 0 ? 1 : 0
  return Math.max(0, Math.min(1, part / whole))
}
